import { ModeEnum, type CustomerQuote } from '@talos/kyoko';
import type { CustomerQuotePricingRequest, ManualRFQPricingFormState } from './types';

export function buildCustomerQuotePricingRequest({
  quote,
  formState,
}: {
  quote: CustomerQuote;
  formState: ManualRFQPricingFormState;
}): CustomerQuotePricingRequest {
  if (!quote.QuoteID) {
    throw new Error('Missing QuoteID for pricing request');
  }

  const sizeCounteringEnabled = quote.NegotiationParameters?.SizeCountering === ModeEnum.Enabled;
  const bidQty =
    sizeCounteringEnabled && formState.compositeBidAsk.bidQty.hasValue
      ? formState.compositeBidAsk.bidQty.value
      : undefined;
  const offerQty =
    sizeCounteringEnabled && formState.compositeBidAsk.askQty.hasValue
      ? formState.compositeBidAsk.askQty.value
      : undefined;

  if (!quote.LegSummary || quote.LegSummary.length === 0) {
    // No legs, just basic bid-ask
    const bidPx = formState.compositeBidAsk.bid.hasValue ? formState.compositeBidAsk.bid.value : undefined;
    const offerPx = formState.compositeBidAsk.ask.hasValue ? formState.compositeBidAsk.ask.value : undefined;
    if (!bidPx && !offerPx) {
      throw new Error('At least one of BidPx or OfferPx is required');
    }

    return {
      QuoteID: quote.QuoteID,
      BidPx: bidPx,
      OfferPx: offerPx,
      BidQty: bidQty,
      OfferQty: offerQty,
    };
  }

  // Else, we have legs. We can either be pricing "per-leg" or "composite".
  // If per-leg, each leg has its own price. Grab and use that. Else, if composite, apply the same price to every leg.
  const legPrices = quote.LegSummary.map(leg => {
    const legBidAsk = formState.legBidAsks.find(lba => lba.symbol === leg.Symbol);

    const bidPx =
      formState.pricingMethod === 'per-leg'
        ? legBidAsk?.bid.hasValue
          ? legBidAsk.bid.value
          : undefined
        : formState.compositeBidAsk.bid.hasValue
          ? formState.compositeBidAsk.bid.value
          : undefined;

    const offerPx =
      formState.pricingMethod === 'per-leg'
        ? legBidAsk?.ask.hasValue
          ? legBidAsk.ask.value
          : undefined
        : formState.compositeBidAsk.ask.hasValue
          ? formState.compositeBidAsk.ask.value
          : undefined;

    return {
      Symbol: leg.Symbol,
      BidPx: bidPx,
      OfferPx: offerPx,
    };
  });

  return {
    QuoteID: quote.QuoteID,
    BidQty: bidQty,
    OfferQty: offerQty,
    LegPrices: legPrices,
  };
}
